import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown } from 'antd';

import MarkdownContent from '../common/MarkdownContent';
import { buildActionMenu } from '../common/actionMenu';
import { Chart } from '../charts/LazyCharts';
import { EXCLAMATION_CIRCLE, KEBAB } from '../icons';
import { chartPath } from '../reports/ChartRow';
import { markdownToPlainText } from '../../utils/markdown';
import { normalizeId } from '../../utils/ids';

/** The one sentence for a chart tile the viewer may not see here (ruled d26, plan §4.5 ⚠). */
export const TILE_UNAVAILABLE = 'This chart is no longer available.';

// The canvas inside a tile: what is left of a short (280px) or tall
// (576px) tile once its title and foot are drawn. A phone's tiles are the
// chart page's phone height, every one full width.
const CANVAS = { 1: 180, 2: 470 };
const PHONE_CANVAS = 240;

/** What a tile is called, for its grip, its ⋯ and the announcer. */
export function tileLabel(tile) {
	if (!tile) return 'Tile';
	if (tile.kind === 'text') {
		const text = markdownToPlainText(tile.markdown || '').trim();
		return text ? `Heading: ${text.slice(0, 60)}` : 'Heading';
	}
	if (tile.available && tile.chart && tile.chart.name) return tile.chart.name;
	return 'Unavailable chart';
}

/**
 * One tile of a dashboard (charts A6, plan §6.5):
 *
 *   a chart       `available` — its name (the door to the chart page), then
 *                 the chart through the lazy boundary with its run's slot:
 *                 every mark a door, a failure said in its own frame, the
 *                 last answer held dimmed while it re-runs (`loading`);
 *   unavailable   ONE state, "This chart is no longer available." — deleted,
 *                 made personal, unreadable and a card that does not reach
 *                 here all read the same (with Remove while arranging);
 *   a heading     its markdown through utils/markdown.js, never raw HTML.
 *
 * While arranging, a tile has a grip (`handle`, from SortableList) and a ⋯
 * (`menuActions`); `refusal` is what a refused save said of this tile.
 * `onEditChart` (chart) is offered beside a failure the chart's own
 * definition caused, to someone who may edit the chart — as on its page.
 */
export default function DashboardTile({
	tile,
	label: givenLabel = null,
	frozen = false,
	result,
	loading = false,
	collectionId,
	arranging = false,
	phone = false,
	handle = null,
	menuActions = null,
	refusal = null,
	onRemove,
	chartContext,
	onEditChart,
	onRetry,
}) {
	const label = givenLabel || tileLabel(tile);
	const menu = arranging && menuActions ? buildActionMenu(menuActions) : null;
	const chart = tile && tile.kind === 'chart' && tile.available ? tile.chart : null;
	const context = chart && typeof chartContext === 'function' ? chartContext(chart) || {} : {};

	const head = (arranging || chart) ? (
		<div className='dashboard-tile__head'>
			{arranging ? handle : null}
			{chart ? (
				<h2 className='dashboard-tile__title'>
					<Link to={chartPath(collectionId, normalizeId(chart._id))}>{chart.name || 'Chart'}</Link>
				</h2>
			) : <span className='dashboard-tile__spacer' />}
			{menu ? (
				<Dropdown trigger={['click']} placement='bottomRight' menu={menu}>
					<Button className='dashboard-tile__more' icon={<KEBAB />} aria-label={`Arrange ${label}`} />
				</Dropdown>
			) : null}
		</div>
	) : null;

	let body;
	if (tile && tile.kind === 'text') {
		body = <MarkdownContent markdown={tile.markdown || ''} className='dashboard-tile__text' />;
	} else if (!chart) {
		body = (
			<div className='dashboard-tile__unavailable'>
				<span>{TILE_UNAVAILABLE}</span>
				{arranging && typeof onRemove === 'function' ? <Button size='small' onClick={onRemove} disabled={frozen}>Remove</Button> : null}
			</div>
		);
	} else {
		body = (
			<Chart
				chart={chart}
				result={result || null}
				loading={loading}
				collectionId={collectionId}
				cardName={context.cardName}
				elements={context.elements}
				showTitle={false}
				headingLevel={3}
				height={phone ? PHONE_CANVAS : (CANVAS[tile.h] || CANVAS[1])}
				onRetry={onRetry}
				onEdit={!arranging && typeof onEditChart === 'function' && chart.canEdit === true ? () => onEditChart(chart) : undefined}
			/>
		);
	}

	return (
		<div className={`dashboard-tile__inner dashboard-tile__inner--${tile ? tile.kind : 'chart'}`}>
			{head}
			{refusal ? (
				<div className='dashboard-tile__refusal' role='alert'>
					<EXCLAMATION_CIRCLE aria-hidden='true' />
					<span>{refusal}</span>
				</div>
			) : null}
			<div className='dashboard-tile__body'>{body}</div>
		</div>
	);
}
